import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Clock, ShieldCheck, Flame } from "lucide-react";
import { Logo } from "../components/Logo";
import { api } from "../lib/api";
import { JOBBER_BOOKING_URL } from "../lib/brand";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/1eff79bd-c15f-46f2-ad35-83de1c0b6070/images/3613f803bff08933ea41ca30a8a5f779bc995766d5d9859120ff49c41558af1f.png";
const BEAD_IMG = "https://static.prod-images.emergentagent.com/jobs/1eff79bd-c15f-46f2-ad35-83de1c0b6070/images/380c62e2d17ab4c0fe14e9b3f37b14157f8f868405c2793a445aa33af5ed3513.png";

const trustItems = [
  { icon: Sparkles, title: "Showroom Finish", desc: "Every detail engineered for that just-off-the-floor gloss." },
  { icon: ShieldCheck, title: "Trusted Care", desc: "Premium products. Hand-picked techniques. Zero shortcuts." },
  { icon: Clock, title: "On Your Schedule", desc: "Flexible bookings that fit your life. We work fast, never rushed." },
];

const HomePage = () => {
  const [services, setServices] = useState([]);
  useEffect(() => { api.get("/services").then(r => setServices(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full grid md:grid-cols-2 gap-12 items-center py-24">
          <div className="slide-up">
            <div className="mb-8"><Logo size="xl" showText /></div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#D90429]/40 bg-[#D90429]/10 mb-6">
              <Flame size={14} className="text-[#D90429]" />
              <span className="font-display uppercase tracking-[0.25em] text-[10px] text-[#D90429]">Premium Auto Detailing</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter uppercase leading-[0.95]">
              Precision Detailing.<br />
              <span className="text-[#D90429]">Premium Results.</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#A3A3A3] max-w-xl leading-relaxed">
              Professional car detailing services built to make your vehicle look its best.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href={JOBBER_BOOKING_URL}
                data-testid="hero-book-now"
                className="group bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold tracking-wider uppercase px-8 py-4 inline-flex items-center gap-2 transition-colors"
              >
                Book Now <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/services"
                data-testid="hero-view-services"
                className="border border-[#262626] hover:border-[#D90429] text-white font-display font-bold tracking-wider uppercase px-8 py-4 inline-flex items-center gap-2 transition-colors"
              >
                View Services
              </Link>
            </div>
          </div>
          <div className="hidden md:block">
            <div className="relative aspect-square">
              <div className="absolute inset-4 border border-[#D90429]/30" />
              <img src={BEAD_IMG} alt="Glossy paint water beading" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute bottom-6 left-6 right-6 bg-black/80 backdrop-blur-sm border border-[#D90429]/40 p-6">
                <div className="font-display uppercase tracking-[0.2em] text-[10px] text-[#D90429] mb-2">Featured</div>
                <div className="font-display text-2xl font-bold">Ceramic-grade gloss</div>
                <div className="text-sm text-[#A3A3A3] mt-1">Hydrophobic. Resilient. Relentless.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-16">
            <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429] mb-3">Why Fuego</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight uppercase">
              Built on detail.<br/>Driven by passion.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-[#262626]">
            {trustItems.map((t) => (
              <div key={t.title} className="bg-[#050505] p-10 hover:bg-[#0a0a0a] transition-colors group" data-testid={`trust-${t.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="w-12 h-12 bg-[#D90429]/10 border border-[#D90429]/40 flex items-center justify-center mb-6 group-hover:bg-[#D90429] transition-colors">
                  <t.icon size={20} className="text-[#D90429] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-display text-xl font-bold uppercase tracking-tight">{t.title}</h3>
                <p className="mt-3 text-sm text-[#A3A3A3] leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES HIGHLIGHT */}
      <section className="py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap justify-between items-end mb-16 gap-6">
            <div>
              <div className="font-display uppercase tracking-[0.25em] text-xs text-[#D90429] mb-3">Services & Pricing</div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight uppercase">Choose Your Package</h2>
            </div>
            <Link to="/services" className="font-display uppercase tracking-wider text-sm hover:text-[#D90429] inline-flex items-center gap-2" data-testid="home-all-services">
              All Services <ArrowRight size={16}/>
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {services.map((s) => (
              <div key={s.id} className="bg-[#121212] border border-[#262626] p-8 hover:border-[#D90429]/60 transition-colors speed-line" data-testid={`home-service-${s.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-display text-xl font-bold uppercase tracking-tight">{s.short_name}</h3>
                  <Flame size={18} className="text-[#D90429]" />
                </div>
                <p className="text-sm text-[#A3A3A3] leading-relaxed min-h-[60px]">{s.description}</p>
                <div className="mt-6 flex items-baseline gap-2">
                  <span className="text-[#A3A3A3] text-sm">{s.has_vehicle_types ? "From" : "Price"}</span>
                  <span className="font-display text-4xl font-black text-white">${s.price.toFixed(0)}</span>
                </div>
                <a href={JOBBER_BOOKING_URL} className="mt-6 block w-full bg-[#D90429] hover:bg-[#EF233C] text-white text-center font-display font-bold tracking-wider uppercase px-6 py-3 transition-colors" data-testid={`home-book-${s.id}`}>
                  Book This Service
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="border border-[#D90429]/40 bg-gradient-to-r from-[#D90429]/10 to-transparent p-12 md:p-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight">Ready for the Fuego finish?</h2>
              <p className="mt-3 text-[#A3A3A3] max-w-xl">Lock in your appointment in under a minute. Pay securely. Drive cleaner.</p>
            </div>
            <a href={JOBBER_BOOKING_URL} data-testid="cta-book-now" className="bg-[#D90429] hover:bg-[#EF233C] text-white font-display font-bold tracking-wider uppercase px-10 py-5 inline-flex items-center gap-2 transition-colors">
              Book Appointment <ArrowRight size={18}/>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
